import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { useParams, useSearchParams } from 'react-router';
import {
  CommentFragment,
  ContactFragment,
  ContractFragment,
  AddContactToContractDocument,
  AddContractCommentDocument,
  AddUnitToContractDocument,
  GetContractDocument,
  GetUnitsDocument,
  RemoveContactFromContractDocument,
  RemoveContractCommentDocument,
  RemoveUnitFromContractDocument,
  UpdateContactInContractDocument,
  UpdateContractCommentDocument,
  UpdateContractDocument,
  AddContactToContractMutation,
  AddContactToContractMutationVariables,
  AddContractCommentMutation,
  AddContractCommentMutationVariables,
  AddUnitToContractMutation,
  AddUnitToContractMutationVariables,
  GetContractQuery,
  GetContractQueryVariables,
  GetUnitsQuery,
  GetUnitsQueryVariables,
  RemoveContactFromContractMutation,
  RemoveContactFromContractMutationVariables,
  RemoveContractCommentMutation,
  RemoveContractCommentMutationVariables,
  RemoveUnitFromContractMutation,
  RemoveUnitFromContractMutationVariables,
  UpdateContactInContractMutation,
  UpdateContactInContractMutationVariables,
  UpdateContractCommentMutation,
  UpdateContractCommentMutationVariables,
  UpdateContractMutation,
  UpdateContractMutationVariables,
  UndeleteContractMutation,
  UndeleteContractMutationVariables,
  UndeleteContractDocument,
} from '../../generated/graphql';

import { useAlert } from '@dwelvio/react-alert';
import { Button, Typography, Stack, Grid, Box, Divider } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';

import { BasePage } from '../../components/base-page';

import { InputContactDialog } from './components/input-contact-dialog';
import { UnitList } from './components/unit-list';

import { SelectUnitDialog } from './components/select-unit-dialog';
import { appPaths } from '../../app-paths';
import { InputContractTermsDialog } from './components/input-contract-terms-dialog';
import { ContactList } from './components/contact-list';
import { ConfirmationDialog } from '../../components/confirmation-dialog';
import { ContractTermsPanel } from './components/contract-terms-panel';
import { CommentList } from '../../components/comments-list';

import { InputComment } from '../../components/input-comment';

import { useApp } from '../../components/app-context';
import { ContractDetailMenu } from './components/contract-detail-menu';
import { RelatedTasksSection } from './components/related-tasks';
import { RelatedInvoicesSection } from './components/related-invoices';
import { DeletedItemBanner } from '../../components/deleted-item-banner';
import { ContractDocumentsSection } from './components/contract-documents-section';

const TaskTabTypesArray = ['tasks', 'recurring'] as const;
type TaskTabType = (typeof TaskTabTypesArray)[number];

const InvoiceTabTypesArray = ['invoices', 'recurring'] as const;
type InvoiceTabType = (typeof InvoiceTabTypesArray)[number];

export function ContractDetailPage() {
  const app = useApp();

  const { contractId } = useParams();
  const { show } = useAlert();
  const [showSelectUnitDialog, setShowSelectUnitDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactFragment | null>(null);
  const [showNewContactDialog, setShowNewContactDialog] = useState(false);
  const [confirmContactRemove, setConfirmContactRemove] = useState<ContactFragment | null>(null);
  const [confirmCommentRemove, setConfirmCommentRemove] = useState<CommentFragment | null>(null);
  const [showModifyTermsDialog, setShowModifyTermsDialog] = useState<ContractFragment | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [fetchContract, { data, loading, error, refetch }] = useLazyQuery<GetContractQuery, GetContractQueryVariables>(
    GetContractDocument,
  );

  const [updateContract] = useMutation<UpdateContractMutation, UpdateContractMutationVariables>(
    UpdateContractDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa uložiť zmeny', e.message),
    },
  );

  const [undeleteContract] = useMutation<UndeleteContractMutation, UndeleteContractMutationVariables>(
    UndeleteContractDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa obnoviť prenájom.', e.message),
    },
  );

  const [addContact] = useMutation<AddContactToContractMutation, AddContactToContractMutationVariables>(
    AddContactToContractDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa pridať kontakt.', e.message),
    },
  );
  const [removeContact] = useMutation<RemoveContactFromContractMutation, RemoveContactFromContractMutationVariables>(
    RemoveContactFromContractDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa odstrániť kontakt.', e.message),
    },
  );
  const [updateContact] = useMutation<UpdateContactInContractMutation, UpdateContactInContractMutationVariables>(
    UpdateContactInContractDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa aktualizovať kontakt.', e.message),
    },
  );

  const [addUnit] = useMutation<AddUnitToContractMutation, AddUnitToContractMutationVariables>(
    AddUnitToContractDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa pridať nehnuteľnosť.', e.message),
    },
  );
  const [removeUnit] = useMutation<RemoveUnitFromContractMutation, RemoveUnitFromContractMutationVariables>(
    RemoveUnitFromContractDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa odstrániť nehnuteľnosť.', e.message),
    },
  );
  const [addComment] = useMutation<AddContractCommentMutation, AddContractCommentMutationVariables>(
    AddContractCommentDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa pridať komentár.', e.message),
    },
  );
  const [updateComment] = useMutation<UpdateContractCommentMutation, UpdateContractCommentMutationVariables>(
    UpdateContractCommentDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa aktualizovať komentár.', e.message),
    },
  );
  const [removeComment] = useMutation<RemoveContractCommentMutation, RemoveContractCommentMutationVariables>(
    RemoveContractCommentDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa odstrániť komentár.', e.message),
    },
  );

  const { data: propsData, error: propsError } = useQuery<GetUnitsQuery, GetUnitsQueryVariables>(GetUnitsDocument);

  useEffect(() => {
    if (contractId) {
      fetchContract({ variables: { id: contractId } });
    }
  }, [contractId, fetchContract]);

  const c = data?.getContract;
  const comments = c?.comments ?? [];
  const contractUnits = c?.units ?? [];
  const accountUnits = propsData?.getUnits ?? [];
  const contractLocked = !!c?.deletedAt;

  const taskTab: TaskTabType = (searchParams.get('tt') as TaskTabType) ?? 'tasks';
  if (taskTab && !TaskTabTypesArray.includes(taskTab as TaskTabType)) {
    searchParams.set('tt', 'tasks');
    setSearchParams(searchParams);
  }

  const invoiceTab: InvoiceTabType = (searchParams.get('it') as InvoiceTabType) ?? 'invoices';
  if (invoiceTab && !InvoiceTabTypesArray.includes(invoiceTab as InvoiceTabType)) {
    searchParams.set('it', 'invoices');
    setSearchParams(searchParams);
  }

  return (
    <BasePage
      title={
        c ? (c.signingDate ? 'Detail prenájmu' : 'Návrh prenájmu') + (c.title ? ` - ${c.title}` : '') : 'Načítavam...'
      }
      loading={loading || !contractId || !c}
      errorMsg={error ? 'Error loading contract' : undefined}
    >
      {c && contractId && (
        <>
          {c.deletedAt && (
            <DeletedItemBanner
              text={`Tento ${c.signingDate ? 'prenájom' : 'návrh prenájmu'} bol zmazaný dňa ${app.formatDate(c.deletedAt)}`}
              undeleteLabel="Obnoviť"
              onUndelete={() => undeleteContract({ variables: { contractId } })}
            />
          )}

          <Grid container rowSpacing={2} columnSpacing={3} sx={{ flexGrow: 1 }}>
            <Grid size={{ xs: 12, sm: 7 }}>
              <Stack spacing={2} sx={{ width: '100%' }}>
                <ContractTermsPanel contract={c} />

                <Stack direction={'row'}>
                  <Button
                    disabled={contractLocked}
                    onClick={() => setShowModifyTermsDialog(c)}
                    variant="outlined"
                    sx={{ marginTop: 2 }}
                  >
                    Uprav podmienky
                  </Button>
                </Stack>

                <Divider sx={{ marginY: 3 }} />
                <Stack direction="row">
                  <Button
                    onClick={() => setShowSelectUnitDialog(true)}
                    disabled={contractLocked || accountUnits.length === contractUnits.length}
                    startIcon={<AddIcon />}
                    variant="outlined"
                  >
                    Pridaj nehnuteľnosť
                  </Button>
                </Stack>

                <UnitList
                  units={contractUnits}
                  onRemove={(p) => removeUnit({ variables: { contractId, unitId: p.id } })}
                  disabled={contractLocked}
                />

                <Divider sx={{ marginY: 3 }} />

                <Stack direction="row">
                  <Button
                    disabled={contractLocked}
                    onClick={() => setShowNewContactDialog(true)}
                    startIcon={<AddIcon />}
                    variant="outlined"
                  >
                    Pridaj kontakt
                  </Button>
                </Stack>
                <ContactList
                  contacts={c.contacts}
                  onRemove={(c) => setConfirmContactRemove(c)}
                  onClick={(c) => setSelectedContact(c)}
                  disabled={contractLocked}
                />

                <Divider sx={{ marginY: 3 }} />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }} rowGap={2}>
              <ContractDetailMenu contract={c} disabled={contractLocked} />
              <Typography variant="h2">Komentáre</Typography>
              <InputComment
                label="Pridať komentár"
                disabled={contractLocked}
                onSubmit={(comment) => addComment({ variables: { contractId, input: { comment } } })}
                multiline
                clearOnSubmit
              />
              <Box maxHeight={{ xs: '20rem', sm: '20rem' }} overflow={{ overflowY: 'scroll' }} sx={{ marginTop: 2 }}>
                <CommentList
                  comments={comments}
                  onDelete={setConfirmCommentRemove}
                  onEdit={(commentId, comment) =>
                    updateComment({ variables: { contractId, input: { id: commentId, comment } } })
                  }
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <ContractDocumentsSection
                contractId={c.id}
                files={c.files}
                documents={c.documents}
                disabled={contractLocked}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <RelatedTasksSection
                contractId={c.id}
                disabled={contractLocked}
                tabValue={taskTab}
                onTabChange={(e, v) => {
                  searchParams.set('tt', v);
                  setSearchParams(searchParams);
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <RelatedInvoicesSection
                contractId={c.id}
                disabled={contractLocked}
                tabValue={invoiceTab}
                onTabChange={(e, v) => {
                  searchParams.set('it', v);
                  setSearchParams(searchParams);
                }}
              />
            </Grid>
          </Grid>

          {showSelectUnitDialog && (
            <SelectUnitDialog
              selected={contractUnits}
              units={accountUnits}
              onSubmit={async (unitId) => {
                await addUnit({ variables: { contractId, unitId } });
                setShowSelectUnitDialog(false);
              }}
              onClose={() => setShowSelectUnitDialog(false)}
            />
          )}
          {confirmContactRemove && (
            <ConfirmationDialog
              onCancel={() => setConfirmContactRemove(null)}
              onConfirm={() => {
                removeContact({ variables: { contractId, contactId: confirmContactRemove.id } });
                setConfirmContactRemove(null);
              }}
              title="Odstránenie kontaktu"
              message={`Naozaj chcete odstrániť kontakt '${confirmContactRemove.name}'?`}
            />
          )}

          {confirmCommentRemove && (
            <ConfirmationDialog
              onCancel={() => setConfirmCommentRemove(null)}
              onConfirm={() => {
                removeComment({ variables: { contractId, commentId: confirmCommentRemove.id } });
                setConfirmCommentRemove(null);
              }}
              title="Odstránenie komentára"
              message={`Naozaj chcete odstrániť komentár '${confirmCommentRemove.comment}'?`}
            />
          )}

          {showNewContactDialog && (
            <InputContactDialog
              onSubmit={(data) => {
                addContact({ variables: { contractId, input: data } });
                setShowNewContactDialog(false);
              }}
              onClose={() => setShowNewContactDialog(false)}
              open={showNewContactDialog}
            />
          )}

          {selectedContact && (
            <InputContactDialog
              contact={selectedContact}
              onSubmit={(data) => {
                updateContact({ variables: { contractId, input: { ...data, id: selectedContact.id } } });
                setSelectedContact(null);
              }}
              onClose={() => setSelectedContact(null)}
              open={!!selectedContact}
            />
          )}

          {showModifyTermsDialog && (
            <InputContractTermsDialog
              open={!!showModifyTermsDialog}
              contract={showModifyTermsDialog}
              onSubmit={(input) => {
                updateContract({ variables: { input: { id: contractId, ...input } } });
                setShowModifyTermsDialog(null);
              }}
              onClose={() => setShowModifyTermsDialog(null)}
            />
          )}
        </>
      )}
    </BasePage>
  );
}
